// src/templateManager/components/tabs/PreviewTab.jsx
import React, { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  Stack,
  Card,
  Text,
  Group,
  Tabs,
  Badge,
  TextInput,
  Textarea,
  MultiSelect,
  Select,
  Accordion,
  Checkbox,
  Alert
} from '@mantine/core';
import { IconInfoCircle, IconEye } from '@tabler/icons-react';

/**
 * PreviewTab - Shows how the template will appear during assessments
 * Live preview of context fields, risk areas, and overall structure
 */
function PreviewTab() {
  const { watch } = useFormContext();
  const [previewSection, setPreviewSection] = useState('context');

  const templateName = watch('name');
  const taxonomy = watch('taxonomy') || {};
  const contextFields = taxonomy.contextFields || {};
  const riskAreas = taxonomy.riskAreas || {};

  // Convert camelCase to Title Case
  const toTitleCase = (str) => str
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/([A-Z]+)([A-Z][a-z])/g, '$1 $2')
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
    .trim();

  // Render context field preview
  const renderFieldPreview = (field, index) => {
    const titleName = toTitleCase(field.name);
    const commonProps = {
      label: titleName,
      description: field.description,
      placeholder: field.placeholder || `Enter ${titleName.toLowerCase()}...`,
      disabled: true
    };

    switch (field.type) {
      case 'textarea':
        return <Textarea key={index} {...commonProps} minRows={3} />;

      case 'dropdown':
        return (
          <Select
            key={index}
            {...commonProps}
            data={field.options || []}
            placeholder={field.placeholder || `Select ${titleName.toLowerCase()}...`}
          />
        );

      case 'multiselect':
        return (
          <MultiSelect
            key={index}
            {...commonProps}
            data={field.options || []}
            placeholder={field.placeholder || `Select ${titleName.toLowerCase()}...`}
          />
        );

      default:
        return <TextInput key={index} {...commonProps} />;
    }
  };

  // Context fields preview
  const renderContextPreview = () => {
    const sections = Object.keys(contextFields);
    const sectionLabels = Object.fromEntries(
      sections.map((key) => [key, toTitleCase(key)])
    );

    return (
      <Stack gap="lg">
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          This is how users will see the context gathering step during assessment creation.
        </Alert>

        {sections.map((sectionKey) => {
          const fields = contextFields[sectionKey] || [];

          if (fields.length === 0) {
            return null;
          }

          return (
            <Card key={sectionKey} withBorder p="lg">
              <Stack gap="md">
                <Group>
                  <Text fw={600} size="lg">{sectionLabels[sectionKey]}</Text>
                  <Badge size="sm" variant="light">{fields.length} fields</Badge>
                </Group>

                <Stack gap="md">
                  {fields.map((field, index) => renderFieldPreview(field, index))}
                </Stack>
              </Stack>
            </Card>
          );
        })}

        {Object.keys(contextFields).every(key => (contextFields[key] || []).length === 0) && (
          <Card withBorder p="xl">
            <Text c="dimmed" ta="center">
              No context fields configured. Add fields in the Context Fields tab.
            </Text>
          </Card>
        )}
      </Stack>
    );
  };

  // Risk areas preview
  const renderRiskAreasPreview = () => {
    const areaKeys = Object.keys(riskAreas);

    if (areaKeys.length === 0) {
      return (
        <Card withBorder p="xl">
          <Text c="dimmed" ta="center">
            No risk areas configured. Add risk areas in the Risk Areas tab.
          </Text>
        </Card>
      );
    }

    return (
      <Stack gap="lg">
        <Alert icon={<IconInfoCircle size={16} />} color="blue">
          This is how users will see risk areas during risk discovery.
        </Alert>

        <Accordion multiple>
          {areaKeys.map((areaKey) => {
            const area = riskAreas[areaKey];
            const sources = area.riskSources || [];

            return (
              <Accordion.Item key={areaKey} value={areaKey}>
                <Accordion.Control>
                  <Group>
                    <div style={{ flex: 1 }}>
                      <Text fw={600}>{area.label}</Text>
                      <Text size="sm" c="dimmed">{area.description}</Text>
                    </div>
                    <Badge size="sm" variant="light">{sources.length} sources</Badge>
                  </Group>
                </Accordion.Control>

                <Accordion.Panel>
                  <Stack gap="md">
                    {sources.length === 0 ? (
                      <Text size="sm" c="dimmed">No risk sources configured</Text>
                    ) : (
                      sources.map((source, index) => (
                        <Card key={index} withBorder p="md">
                          <Stack gap="sm">
                            <Text fw={500}>{source.name}</Text>
                            {source.description && (
                              <Text size="sm" c="dimmed">{source.description}</Text>
                            )}

                            {source.type === 'multiselect' && source.options && (
                              <MultiSelect
                                label="Select applicable risks"
                                data={source.options}
                                placeholder="Select risks..."
                                disabled
                              />
                            )}

                            {source.type === 'text' && (
                              <Textarea
                                label="Describe risks"
                                placeholder="Enter risk details..."
                                disabled
                                minRows={2}
                              />
                            )}
                          </Stack>
                        </Card>
                      ))
                    )}
                  </Stack>
                </Accordion.Panel>
              </Accordion.Item>
            );
          })}
        </Accordion>
      </Stack>
    );
  };

  // Summary preview
  const renderSummaryPreview = () => {
    const totalContextFields = Object.values(contextFields).reduce(
      (sum, fields) => sum + (fields?.length || 0),
      0
    );
    const totalRiskAreas = Object.keys(riskAreas).length;
    const totalRiskSources = Object.values(riskAreas).reduce(
      (sum, area) => sum + (area.riskSources?.length || 0),
      0
    );

    return (
      <Stack gap="lg">
        <Card withBorder p="lg" bg="blue.0">
          <Stack gap="md">
            <Group>
              <IconEye size={24} />
              <Text fw={700} size="lg">Template Preview</Text>
            </Group>
            <Text size="sm" c="dimmed">
              Overview of what users will experience when using this template
            </Text>
          </Stack>
        </Card>

        <Card withBorder p="lg">
          <Stack gap="md">
            <Text fw={600} size="lg">Template Information</Text>
            <Group gap="xl">
              <div>
                <Text size="sm" c="dimmed">Template Name</Text>
                <Text fw={500}>{templateName || 'Untitled Template'}</Text>
              </div>
            </Group>
          </Stack>
        </Card>

        <Card withBorder p="lg">
          <Stack gap="md">
            <Text fw={600} size="lg">Assessment Flow</Text>

            <Group gap="md">
              <Badge size="lg" variant="light">
                Step 1: Context Gathering
              </Badge>
              <Text size="sm">{totalContextFields} fields</Text>
            </Group>

            <Group gap="md">
              <Badge size="lg" variant="light" color="orange">
                Step 2: Risk Discovery
              </Badge>
              <Text size="sm">{totalRiskAreas} areas • {totalRiskSources} sources</Text>
            </Group>

            <Group gap="md">
              <Badge size="lg" variant="light" color="green">
                Step 3: Risk Evaluation
              </Badge>
              <Text size="sm">Using centrally managed criteria</Text>
            </Group>

            <Group gap="md">
              <Badge size="lg" variant="light" color="red">
                Step 4: Security Assessment
              </Badge>
              <Text size="sm">Security threat taxonomy</Text>
            </Group>
          </Stack>
        </Card>

        <Card withBorder p="lg" bg="gray.0">
          <Stack gap="xs">
            <Text size="sm" fw={600}>Completeness Check</Text>
            <Group gap="md">
              <Checkbox
                checked={totalContextFields > 0}
                label="Context fields configured"
                disabled
              />
              <Checkbox
                checked={totalRiskAreas > 0}
                label="Risk areas configured"
                disabled
              />
              <Checkbox
                checked={taxonomy.rootCauses && Object.keys(taxonomy.rootCauses).length > 0}
                label="Root causes configured"
                disabled
              />
              <Checkbox
                checked={taxonomy.securityTaxonomy && Object.keys(taxonomy.securityTaxonomy).length > 0}
                label="Security taxonomy configured"
                disabled
              />
            </Group>
          </Stack>
        </Card>
      </Stack>
    );
  };

  return (
    <Stack gap="lg">
      <Tabs value={previewSection} onChange={setPreviewSection}>
        <Tabs.List>
          <Tabs.Tab value="summary">
            Summary
          </Tabs.Tab>
          <Tabs.Tab value="context">
            Context Fields
          </Tabs.Tab>
          <Tabs.Tab value="risks">
            Risk Areas
          </Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="summary" pt="lg">
          {renderSummaryPreview()}
        </Tabs.Panel>

        <Tabs.Panel value="context" pt="lg">
          {renderContextPreview()}
        </Tabs.Panel>

        <Tabs.Panel value="risks" pt="lg">
          {renderRiskAreasPreview()}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}

export default PreviewTab;
